import React, { useState, useEffect, useRef } from "react";
import { SearchOutlined, CloseOutlined } from "@ant-design/icons";
import "./SearchPopup.css";

interface SearchPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSearch: (query: string) => void;
}

const SearchPopup: React.FC<SearchPopupProps> = ({ isOpen, onClose, onSearch }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when popup opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // ESC key closes popup and prevent body scroll when open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onSearch(searchQuery.trim());
      onClose();
    }
  };

  // ✅ FIX: prevent close button and overlay from both firing
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // close only if clicked *directly* on overlay (not content or child)
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="search-popup-overlay" onClick={handleOverlayClick}>
      <div className="search-popup-content">
        <div className="search-popup-header">
          <p className="search-popup-instruction">Press ESC to close</p>
          <button
            className="search-popup-close"
            onClick={(e) => {
              e.stopPropagation(); // stop bubbling to overlay
              onClose();
            }}
            aria-label="Close search"
          >
            <CloseOutlined />
          </button>
        </div>

        <form className="search-popup-form" onSubmit={handleSubmit}>
          <div className="search-popup-input-container">
            <input
              ref={inputRef}
              type="text"
              className="search-popup-input"
              placeholder="Search and press enter ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              type="submit"
              className="search-popup-button"
              aria-label="Search"
            >
              <SearchOutlined />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SearchPopup;
